import { deleteReport } from "@/api/endpoints/reports";
import {
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@components/ui/alert-dialog";
import { Spinner } from "@components/ui/spinner";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type DeleteReportDialogProps = {
  orgUuid: Uuid;
  reportUuid?: Uuid;
  onClose: () => void;
};

export default function DeleteReportDialog({
  reportUuid,
  orgUuid,
  onClose,
}: DeleteReportDialogProps) {
  const { t } = useLingui();

  // Server state
  const queryClient = useQueryClient();

  const { mutate: mutateDeleteReport, isPending: isDeleteReportPending } =
    useMutation({
      mutationFn: deleteReport,
      onSuccess: (res) => {
        if (!res.ok) {
          throw new Error();
        }

        toast.success(t`Raderade rapporten`);

        queryClient.invalidateQueries({
          queryKey: [orgUuid, "reports"],
        });

        onClose();
      },
      onError: () => {
        toast.error(t`Lyckades inte radera rapporten`);
      },
    });

  return (
    <AlertDialog open={!!reportUuid} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Trans>Radera rapport?</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <Trans>
              Detta kommer att radera rapporten permanent för alla som
              prenumererar. Den här åtgärden kan inte ångras.
            </Trans>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleteReportPending}>
            <Trans>Avbryt</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isDeleteReportPending}
            onClick={() => {
              if (!reportUuid) {
                return;
              }

              mutateDeleteReport(reportUuid);
            }}
          >
            <Trans>Radera</Trans>
            {isDeleteReportPending && <Spinner />}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
